export interface RelatedEntityAdjustment {
  plusCash: number;
  lessDebt: number;
}

export type RelatedEntityAdjustments = Record<number, RelatedEntityAdjustment>;

export interface SerializedRelatedEntityAdjustment {
  plus_cash?: unknown;
  less_debt?: unknown;
}

export type SerializedRelatedEntityAdjustments = Record<string, SerializedRelatedEntityAdjustment>;

export type SavedRelatedEntityAdjustments = Record<
  string,
  {
    plus_cash: string;
    less_debt: string;
  }
>;

const parseFiniteNumber = (value: unknown, fallback = 0): number | undefined => {
  if (value === undefined) {
    return fallback;
  }
  if ((typeof value !== 'number' && typeof value !== 'string') || value === '') {
    return undefined;
  }
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : undefined;
};

export const parseRelatedEntityAdjustments = (value: unknown): RelatedEntityAdjustments => {
  if (typeof value !== 'string') {
    return {};
  }

  try {
    const parsedValue: unknown = JSON.parse(value);
    if (!parsedValue || typeof parsedValue !== 'object' || Array.isArray(parsedValue)) {
      return {};
    }

    const adjustments = Object.entries(parsedValue).reduce<RelatedEntityAdjustments | null>(
      (result, [companyIdValue, adjustmentValue]) => {
        if (result === null) {
          return null;
        }
        const companyId = Number(companyIdValue);
        if (
          !Number.isSafeInteger(companyId) ||
          companyId <= 0 ||
          !adjustmentValue ||
          typeof adjustmentValue !== 'object' ||
          Array.isArray(adjustmentValue)
        ) {
          return null;
        }

        const serializedAdjustment = adjustmentValue as SerializedRelatedEntityAdjustment;
        const plusCash = parseFiniteNumber(serializedAdjustment.plus_cash);
        const lessDebt = parseFiniteNumber(serializedAdjustment.less_debt);
        if (plusCash === undefined || lessDebt === undefined) {
          return null;
        }

        return { ...result, [companyId]: { plusCash, lessDebt } };
      },
      {}
    );

    return adjustments ?? {};
  } catch {
    return {};
  }
};

export const serializeRelatedEntityAdjustments = (
  adjustments: RelatedEntityAdjustments
): string | undefined => {
  const serializedAdjustments = Object.entries(adjustments)
    .map(([companyIdValue, adjustment]) => [Number(companyIdValue), adjustment] as const)
    .filter(
      ([companyId, adjustment]) =>
        Number.isSafeInteger(companyId) &&
        companyId > 0 &&
        Number.isFinite(adjustment.plusCash) &&
        Number.isFinite(adjustment.lessDebt) &&
        (adjustment.plusCash !== 0 || adjustment.lessDebt !== 0)
    )
    .sort(([leftCompanyId], [rightCompanyId]) => leftCompanyId - rightCompanyId)
    .reduce<Record<string, { plus_cash: string; less_debt: string }>>(
      (result, [companyId, adjustment]) => ({
        ...result,
        [companyId]: {
          plus_cash: String(adjustment.plusCash),
          less_debt: String(adjustment.lessDebt),
        },
      }),
      {}
    );

  return Object.keys(serializedAdjustments).length > 0 ? JSON.stringify(serializedAdjustments) : undefined;
};

export const parseSavedRelatedEntityAdjustments = (
  adjustments: SerializedRelatedEntityAdjustments | null | undefined
): RelatedEntityAdjustments => parseRelatedEntityAdjustments(JSON.stringify(adjustments ?? {}));

export const serializeSavedRelatedEntityAdjustments = (
  adjustments: RelatedEntityAdjustments
): SavedRelatedEntityAdjustments => {
  const serializedAdjustments = serializeRelatedEntityAdjustments(adjustments);
  return serializedAdjustments
    ? (JSON.parse(serializedAdjustments) as SavedRelatedEntityAdjustments)
    : {};
};
